import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CaptionProps, DayPicker, useNavigation } from 'react-day-picker';
import cn from 'classnames';
import { format, subMonths, subDays, startOfDay, endOfDay } from 'date-fns';
import { ru, tr, zhCN, enUS } from 'date-fns/locale';
import { formatDate } from '@utils/formatValues';
import { getCurrentLanguage, Langs } from '@utils/lang';
import DropdownSimple from "@components/DropdownSimple";
import TextInput from "@components/TextInput/TextInput";
import { IconButton, IconButtonSize, IconButtonType } from "@components/new/IconButton";
import { P2 } from "@components/new/Typo";

import { ToggleButtons, ToggleButtonTypes } from '@components/ToggleButtons'; // todo

// import CrossIcon from '@assets/icons/cross-grey-12.svg';
import CalendarIcon from "@assets/icons/outline/calendar-days.svg?react";
import chevronLeft from '@assets/icons/outline/chevron-left.svg?react';
import chevronRight from '@assets/icons/outline/chevron-right.svg?react';
// import { IDateRangeInputProps } from '@containers/Filter/types';
import styles from "./DateRangeInput.module.scss";
import 'react-day-picker/dist/style.css';

type ValueType = null | Date;

interface IDateRangeInputProps {
  className?: string;
  customDateFormat?: string;
  onChange: (dates: ValueType[]) => void;
  placeholder?: string;
  showTabs?: boolean;
  value: ValueType[];
  disabled?: boolean;
  noIcon?: boolean;
}

const locales = {
  cn: zhCN,
  en: enUS,
  ru: ru,
  tr: tr,
};

type InputValueType = undefined | Date
type RangeType = {
  from?: InputValueType,
  to?: InputValueType,
}

const DateRangeInput = ({
  className,
  customDateFormat,
  onChange,
  placeholder,
  showTabs,
  value,
  disabled,
  noIcon,
} : IDateRangeInputProps) => {
  const currentLanguage = getCurrentLanguage();
  // const [dropdownPosition, setDropdownPosition] = useState<"right" | "left">("right");
  const [startDate, setStartDate_] = useState<InputValueType>(undefined);
  const [endDate, setEndDate_] = useState<InputValueType>(undefined);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [monthL, setMonthL] = useState<Date>(subMonths(new Date(), 1));
  const [monthR, setMonthR] = useState<Date>(new Date());
  const [activePreset, setActivePreset] = useState<string>("");
  const { t } = useTranslation();

  const setStartDate = (v: InputValueType) => setStartDate_(v ? startOfDay(v) : v);
  const setEndDate = (v: InputValueType) => setEndDate_(v ? endOfDay(v) : v);

  const dateFormat = customDateFormat ?? (currentLanguage === Langs.ru ? "d MMM, yyyy" : "MMM d, yyyy");

  const onChangeRange = (range?: RangeType) => {
    setStartDate(range?.from);
    setEndDate(range?.to);
  }

  useEffect(() => {
    setStartDate(value[0] || undefined);
    setEndDate(value[1] || undefined);
  }, [value[0], value[1]])

  // useEffect(() => {
  //   if (elementRef.current) {
  //     const rect = elementRef.current.getBoundingClientRect();
  //     if ((screen.width - rect.right) < 580) setDropdownPosition("left")
  //   }
  // }, []);

  useEffect(() => {
    if (!isOpen) {
      if (!startDate && !endDate) {
        onChange([null, null]);
      } else if (!startDate || !endDate) {
        setStartDate(value[0] || undefined);
        setEndDate(value[1] || undefined);
      } else {
        onChange([startDate, endDate]);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (monthL > monthR) {
      const tmpMonth = monthL;
      setMonthL(monthR);
      setMonthR(tmpMonth);
    }
  }, [monthL, monthR]);

  const hanlePreset = (v: string) => {
    setActivePreset(v);
    const today = new Date();
    switch (v) {
      case "today":
        setMonthL(subMonths(today, 1));
        setMonthR(today);
        setStartDate(today);
        setEndDate(today);
        break;
      case "week":
        setMonthL(subDays(today, 6));
        setMonthR(today);
        setStartDate(subDays(today, 6));
        setEndDate(today);
        break;
      case "month":
        setMonthL(subMonths(today, 1));
        setMonthR(today);
        setStartDate(subMonths(today, 1));
        setEndDate(today);
        break;
      default:
        setMonthL(subMonths(today, 1));
        setMonthR(today);
        setStartDate(undefined);
        setEndDate(undefined);
    }
  }

  return (
    <div className={cn(styles.dateRangeInput, className)} ref={dropdownRef}>
      <DropdownSimple
        triggerContent={(
          <TextInput
            className={styles.input}
            type="text"
            value={(startDate || endDate) ? (
              `${formatDate(startDate, dateFormat)} - ${formatDate(endDate, dateFormat)}`
            ) : ""}
            placeholder={placeholder}
            onChange={() => {}}
            prefix={noIcon ? undefined : <CalendarIcon />}
            onFocus={() => setIsOpen(true)}
            onClick={(e: any) => {
              e.stopPropagation();
            }}
            disabled={disabled}
          />
        )}
        dropdownContent={(
          <div className={styles.dropdown}>
            {showTabs && (
              <div className={styles.presets}>
                <ToggleButtons
                  value={activePreset}
                  onChange={hanlePreset}
                  items={[
                    { id: "all", title: t("dateRangeInput.allDates") },
                    { id: "today", title: t("dateRangeInput.today") },
                    { id: "week", title: t("dateRangeInput.week") },
                    { id: "month", title: t("dateRangeInput.month") },
                  ]}
                  type={ToggleButtonTypes.PRIMARY}
                />
              </div>
            )}
            <div className={cn(
              styles.calendars,
              (startDate === endDate) ? styles.calendarsSingleDay : styles.calendarsMultipleDays
            )}>
              <DayPicker
                mode="range"
                selected={{ from: startDate, to: endDate }}
                onSelect={onChangeRange}
                locale={locales[currentLanguage]}
                month={monthL}
                onMonthChange={setMonthL}
                components={{
                  Caption: CustomCaptionComponent
                }}
              />
              <DayPicker
                mode="range"
                selected={{ from: startDate, to: endDate }}
                onSelect={onChangeRange}
                locale={locales[currentLanguage]}
                month={monthR}
                onMonthChange={setMonthR}
                components={{
                  Caption: CustomCaptionComponent
                }}
              />
            </div>
          </div>
        )}
        menuAlign="left"
        menuPosition="bottom"
        disabled={disabled}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </div>
  );
}

function CustomCaptionComponent(props: CaptionProps) {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  const currentLanguage = getCurrentLanguage();

  return (
    <div className={styles.calendarCaption}>
      <IconButton
        size={IconButtonSize.L}
        type={IconButtonType.ICON}
        disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
        icon={chevronLeft}
      />
      <P2 weight="bold">{format(props.displayMonth, "LLLL yyy", { locale: locales[currentLanguage] })}</P2>
      <IconButton
        size={IconButtonSize.L}
        type={IconButtonType.ICON}
        disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
        icon={chevronRight}
      />
    </div>
  );
}

export default DateRangeInput;
